import React, { useState, useEffect } from 'react';
import { Card, Typography, Form, Input, Button, Alert, Result } from 'antd';
import { LockOutlined, CheckCircleOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../config/api';
import toast from 'react-hot-toast';

const { Title, Text } = Typography;

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Get token from URL and decode it (in case it was URL-encoded)
  const rawToken = searchParams.get('token');
  const token = rawToken ? decodeURIComponent(rawToken).trim() : null;

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset.');
    } else {
      // Log token for debugging (first 10 chars only for security)
      console.log('[ResetPassword] Token received:', { 
        length: token.length, 
        preview: token.substring(0, 10) + '...',
        rawLength: rawToken?.length
      });
    }
  }, [token, rawToken]);

  const handleSubmit = async (values: any) => {
    if (!token) {
      setError('Invalid or missing reset token');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Ensure token is trimmed and properly formatted
      const cleanToken = token?.trim();
      
      if (!cleanToken || cleanToken.length < 32) {
        setError('Invalid reset token format. Please request a new password reset.');
        toast.error('Invalid reset token format');
        return;
      }
      
      await api.post('/api/auth/reset-password', {
        token: cleanToken,
        new_password: values.new_password,
      });

      setSuccess(true);
      toast.success('Password reset successfully!');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to reset password';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          padding: 24,
        }}
      >
        <Card style={{ width: '100%', maxWidth: 500, textAlign: 'center' }}>
          <Result
            icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            title="Password Reset Successful!"
            subTitle="Your password has been successfully reset. You will be redirected to the login page shortly."
            extra={
              <Button type="primary" size="large" onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5',
        padding: 24,
      }}
    >
      <Card style={{ width: '100%', maxWidth: 450 }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
          <LockOutlined style={{ fontSize: 24, marginRight: 12, color: '#1976d2' }} />
          <Title level={3} style={{ margin: 0 }}>
            Reset Password
          </Title>
        </div>

        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          Enter your new password below. Make sure it's at least 6 characters long. This reset link is valid for 24 hours.
        </Text>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={() => setError('')}
            style={{ marginBottom: 24 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          size="large"
        >
          <Form.Item
            name="new_password"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter your new password' },
              { min: 6, message: 'Password must be at least 6 characters long' },
            ]}
          >
            <Input.Password
              placeholder="Enter new password"
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label="Confirm New Password"
            dependencies={['new_password']}
            rules={[
              { required: true, message: 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password
              placeholder="Confirm new password"
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={!token}
              block
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="link"
              onClick={() => navigate('/login')}
              disabled={loading}
              block
            >
              Back to Login
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default ResetPassword;
